import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { isLoggedIn } from '../../../utils/LoginUtils';
import { KEYS } from '../../../utils/constants/storage.constants';
import { getBaseRequestData } from '../../../utils/evergentUtils';
import { ConfigService } from '../../config.service';
import { LocalStorageService } from '../../storage/local-storage.service';
import { RemoveDeviceRequestMessage } from './model/remove-device.request.model';
import { RemoveDeviceResponseMessage } from './model/remove-device.response.model';

/**
 * Constants for HTTP headers and storage keys
 */
const HTTP_HEADERS = {
  AUTHORIZATION: 'Authorization',
  CONTENT_TYPE: 'Content-Type',
} as const;

/**
 * Service responsible for managing account devices.
 * Handles operations related to device management for user accounts,
 * including device removal and device status updates.
 *
 * @class AccountDevicesService
 */
@Injectable({
  providedIn: 'root',
})
export class AccountDevicesService {
  /** Base URL for UMS API endpoints */
  private umsUrl: string;

  /**
   * Initializes the service with required dependencies
   * @param http - HTTP client for making API requests
   * @param configService - Service for accessing application configuration
   * @param localStorage - Service for managing local storage
   */
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private localStorage: LocalStorageService
  ) {
    this.umsUrl = this.configService.applicationConfig.UmsURL;
  }

  /**
   * Creates HTTP headers with authorization and content type
   * Adds authorization header only if user is logged in
   * @returns HttpHeaders - Headers for API requests
   */
  private getHeaders(): HttpHeaders {
    const headers = new HttpHeaders({
      [HTTP_HEADERS.CONTENT_TYPE]: 'application/json',
    });

    if (isLoggedIn()) {
      return headers.append(
        HTTP_HEADERS.AUTHORIZATION,
        `Bearer ${this.localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN)}`
      );
    }

    return headers;
  }

  /**
   * Handles HTTP errors
   * @param error - The HTTP error response
   * @returns Observable<never> - Error observable
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred while removing the device';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Removes a device from the user's account
   * Makes an API request to remove the specified device
   * @param serialNo - The serial number of the device to remove
   * @returns Observable<RemoveDeviceResponseMessage> - Response from the remove device operation
   * @throws Error if the device removal fails
   */
  public removeDevice(
    serialNo: string
  ): Observable<RemoveDeviceResponseMessage> {
    const url = `${this.umsUrl}/aha/removeDeviceForAccount`;
    const requestContent = new RemoveDeviceRequestMessage();

    // Prepare request content with base data and serial number
    requestContent.RemoveDeviceReqMessage = {
      ...getBaseRequestData(this.configService.applicationConfig),
      serialNo,
    };

    return this.http
      .post<RemoveDeviceResponseMessage>(url, requestContent, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }
}
